// Environment accessors for browser/node contexts with safe defaults

let warnedMissingSalt = false;

function isBrowser(): boolean {
	return typeof window !== 'undefined' && typeof (window as any).document !== 'undefined';
}

export function getNodeEnv(): string {
	const env = (typeof process !== 'undefined' && (process as any)?.env?.NODE_ENV) ? (process as any).env.NODE_ENV : 'development';
	return env || 'development';
}

export function isDev(): boolean {
	return getNodeEnv() !== 'production';
}

export function getHashSalt(): string {
	// In browser builds, only REACT_APP_* vars are embedded. We intentionally do not
	// require a client salt; prefer server hashing. For local dev, an empty salt is acceptable.
	const envObj = (typeof process !== 'undefined') ? (process as any).env || {} : {};
	const fromServer = envObj.HASH_SALT as string | undefined;
	const fromClient = envObj.REACT_APP_HASH_SALT as string | undefined;
	const salt = fromServer || fromClient || '';
	return salt;
}

export function warnIfMissingHashSalt(): void {
	if (warnedMissingSalt) return;
	if (!getHashSalt() && isDev()) {
		// Do not print actual email or secrets; this is a general dev warning.
		try { console.warn('[identity] HASH_SALT is not set. Using unsalted hashes in dev. Avoid setting client salt in production.'); } catch {}
		warnedMissingSalt = true;
	}
}

export function getAdminEmails(): string[] {
	const envObj = (typeof process !== 'undefined') ? (process as any).env || {} : {};
	const raw = (envObj.ADMIN_EMAILS as string | undefined) || (envObj.REACT_APP_ADMIN_EMAILS as string | undefined) || '';
	return raw
		.split(',')
		.map((s) => s.trim())
		.filter((s) => s.length > 0);
}

export type MappingMode = 'off' | 'shadow' | 'enforce';

export function getEmailUidMappingMode(): MappingMode {
	const envObj = (typeof process !== 'undefined') ? (process as any).env || {} : {};
	const raw = (envObj.ENABLE_EMAIL_UID_MAPPING as string | undefined) || (envObj.REACT_APP_ENABLE_EMAIL_UID_MAPPING as string | undefined) || 'enforce';
	const v = String(raw).toLowerCase();
	if (v === 'false' || v === 'off') return 'off';
	if (v === 'shadow') return 'shadow';
	// Default to enforce to prevent duplicate accounts
	return 'enforce';
}

export function isMergeRealRunEnabled(): boolean {
	const envObj = (typeof process !== 'undefined') ? (process as any).env || {} : {};
	const raw = (envObj.MERGE_REAL_RUN as string | undefined) || (envObj.REACT_APP_MERGE_REAL_RUN as string | undefined) || 'false';
	return String(raw).toLowerCase() === 'true';
}

export function isCanonicalAuthEnabled(): boolean {
	const envObj = (typeof process !== 'undefined') ? (process as any).env || {} : {};
	const raw = (envObj.REACT_APP_CANONICAL_AUTH as string | undefined) || 'true';
	const v = String(raw).toLowerCase();
	return !(v === 'false' || v === '0' || v === 'off');
}

export const env = {
	isBrowser: isBrowser(),
	isDev: isDev(),
	getNodeEnv,
	getHashSalt,
	warnIfMissingHashSalt,
	getAdminEmails,
	getEmailUidMappingMode,
	isMergeRealRunEnabled,
	isCanonicalAuthEnabled,
};
